/* WAVE Hair Extensions - keep the WhatsApp widget off the Add to Cart bar
   Built 11 October 2026.

   WHAT WAS WRONG
   Two separate things, both found by reading wave-whatsapp.js against the
   live product page. Daniel reported the symptom on 11 October: the WhatsApp
   button and its label sitting over the Add to Cart button on a phone.

   1. THE LIFT IS SHORT BY ABOUT 11px.
      wave-whatsapp.js lifts the button to bottom:74px while the sticky add to
      cart bar is on screen. That 74px was not arbitrary. The note in that
      file says it was measured in September against a bar 60px tall, leaving
      about 14px of clearance.

      The bar is no longer 60px. Measured on the live product page on
      11 October it is position:fixed, bottom:0, height 85px. So the button's
      lower edge lands at 74px, which is INSIDE the bar's 85px band, and
      because the button is a 56px circle at right:16px it sits over roughly
      the last 72px of the bar. That is precisely where the right hand end of
      the Add to Cart button is.

      Rather than swap one hardcoded number for another that will rot the same
      way, this measures the bar's real height on every check and sets
      --wave-wa-bar from it. If the bar is restyled, or the sticky layout
      setting changes, or Unsen ships a new version, the lift follows on its
      own.

   2. THE NUDGE APPEARS ON A TIMER, NOT IN CONTEXT.
      wave-whatsapp.js shows the "Not sure which shade? Ask us" label nine
      seconds after page load (NUDGE_MS = 9000), with no check on what is
      currently on screen. It is a panel up to 190px wide sitting directly
      above the button. If those nine seconds happen to land while somebody is
      reaching for Add to Cart, the label appears under their thumb. That is
      the "popping up and covering add to cart" Daniel described.

      This hides the nudge whenever the add to cart bar is on screen, and lets
      it come back when the bar is not.

   WHY IT MEASURES THE BAR DIFFERENTLY TO wave-whatsapp.js
   watchStickyBar in that file decides the bar is showing when it has a real
   height and its top edge is above the bottom of the window. But the theme
   hides this bar with the utility classes t4s-op-0 and t4s-pe-none, which
   change opacity and pointer-events and leave the rectangle exactly where it
   was at bottom:0. So that test can read as true while the bar is invisible.
   This file checks opacity, pointer-events, display and visibility as well as
   the rectangle, so it only counts the bar when a customer can actually see
   and touch it. The :not(.wave-atc-on) rule below also cancels the other
   file's lift in that case, so the button cannot float up the page over the
   shade swatches, which is the problem the 19 September note was written to
   solve.

   WHAT IT DELIBERATELY DOES NOT DO
   It does not hide the WhatsApp button while the bar is up. The button lifts
   clear instead, because colour match over WhatsApp is a deliberate route to
   a sale (see the 1 October colour match note) and there were 81
   whatsapp_click events in the six weeks to 9 October, so people do use it.
   If that turns out to be the wrong call, set HIDE_WHILE_BAR to true below
   and the button hides entirely while the bar is on screen.

   SCOPE
   Phones and tablets only (max-width:1024px), matching the existing lift
   rules in wave-whatsapp.js and wave-wa-teaser-lift.js. Desktop is left
   exactly as it was. Desktop has its own, much worse problem (383 sessions
   and zero orders in the six weeks to 9 October) which is being looked at
   separately, and mixing the two changes would make it impossible to tell
   which one moved the numbers.

   WHY A SEPARATE FILE
   Same reasoning as wave-wa-teaser-lift.js: wave-whatsapp.js is not touched,
   so this is a one file rollback and a theme or widget update cannot silently
   undo it.

   LOAD ORDER
   Must come after wave-whatsapp.js and after wave-wa-teaser-lift.js in
   theme.liquid. The selectors carry an extra class on the wrapper so they win
   on specificity rather than on source order, but the measuring still needs
   the widget to have been built.

   VERIFIED 11 October 2026 on the draft theme: script loaded, style injected,
   --wave-wa-bar resolved to 99px (the measured 85px bar plus the 14px gap),
   classes wave-atc-guard has-bar wave-atc-on applied.
*/
(function () {
  if (window.__waveWaAtcGuard) return;
  window.__waveWaAtcGuard = true;

  var HIDE_WHILE_BAR = false; /* true = hide the button entirely while the bar is up */
  var GAP = 14;               /* clear air between the top of the bar and the circle */

  var css = ''
    + '@media (max-width:1024px){'
    /* .wave-atc-guard is added to the wrapper purely to out-specify the bottom
       rules in wave-whatsapp.js and wave-wa-teaser-lift.js. */
    +   'body #wave-wa.wave-atc-guard.wave-atc-on{'
    +     'bottom:calc(var(--wave-wa-bar,99px) + var(--wave-wa-teaser,0px) + env(safe-area-inset-bottom,0px))}'
    /* cancel the other files' lift when the bar is not really visible, so the
       button stays low in the corner where a chat button belongs */
    +   'body #wave-wa.wave-atc-guard:not(.wave-atc-on){'
    +     'bottom:calc(20px + var(--wave-wa-teaser,0px) + env(safe-area-inset-bottom,0px))}'
    /* two ids plus two classes comfortably beats #wave-wa-nudge.is-in,
       so this needs no !important */
    +   'body #wave-wa.wave-atc-guard.wave-atc-on #wave-wa-nudge{'
    +     'opacity:0;pointer-events:none}'
    +   (HIDE_WHILE_BAR
          ? 'body #wave-wa.wave-atc-guard.wave-atc-on{opacity:0;pointer-events:none}'
          : '')
    + '}';

  function addStyle() {
    if (document.getElementById('wave-wa-atc-guard-css')) return;
    var s = document.createElement('style');
    s.id = 'wave-wa-atc-guard-css';
    s.textContent = css;
    document.head.appendChild(s);
  }

  /* The bar's rectangle, but only when a customer could actually see and
     touch it. Returns null otherwise. */
  function visibleBarRect() {
    var bar = document.querySelector('[data-sticky-addtocart]');
    if (!bar) return null;

    var s = getComputedStyle(bar);
    if (s.display === 'none') return null;
    if (s.visibility === 'hidden') return null;
    if (parseFloat(s.opacity || '1') < 0.05) return null;  /* t4s-op-0 */
    if (s.pointerEvents === 'none') return null;           /* t4s-pe-none */

    var r = bar.getBoundingClientRect();
    if (r.height < 1 || r.width < 1) return null;
    if (r.top >= window.innerHeight - 2) return null;      /* pushed off the bottom */
    return r;
  }

  var queued = false;

  function measure() {
    queued = false;

    var wrap = document.getElementById('wave-wa');
    if (!wrap) return;
    wrap.classList.add('wave-atc-guard');

    var r = visibleBarRect();
    if (!r) {
      wrap.classList.remove('wave-atc-on');
      return;
    }

    /* How much of the bottom of the screen the bar covers. Using the bar's
       top edge rather than its height means a bar that is partly scrolled or
       translated still gives the right answer. */
    var covered = Math.round(window.innerHeight - r.top);
    if (covered < 0) covered = 0;

    wrap.style.setProperty('--wave-wa-bar', (covered + GAP) + 'px');
    wrap.classList.add('wave-atc-on');
  }

  function queue() {
    if (queued) return;
    queued = true;
    if (window.requestAnimationFrame) window.requestAnimationFrame(measure);
    else measure();
  }

  function start() {
    addStyle();
    measure();
    window.addEventListener('scroll', queue, { passive: true });
    window.addEventListener('resize', queue, { passive: true });
    window.addEventListener('orientationchange', queue);

    /* The widget appears after a four second delay and the theme builds the
       bar after this script runs, so poll for the first twenty seconds and
       then leave it to scroll and resize. Same shape as the polling in
       wave-wa-teaser-lift.js, deliberately, so the two behave alike. */
    var tries = 0;
    var early = setInterval(function () {
      measure();
      if (++tries > 40) clearInterval(early);
    }, 500);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
